import { useCallback, useMemo } from 'react';
import { HighwayArea } from '@thive/fddm-types';

import { SelectInput } from '@common/components/inputs';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { getCommonOption } from '@/modules/common/helpers/commonValue';
import {
  createEmptyAreas,
  pushAreaToItsCollection,
} from '@/modules/floorplanDesign/helpers/helpers';
import { useTranslatedOptions } from '@/modules/common/hooks';

const OPTIONS: { labelKey: string; value: HighwayArea['connectNeighbors'] }[] = [
  {
    labelKey: 'interface:properties.highway.connection_options.curved',
    value: 'MAP_NEIGHBORS_WITHOUT_PIVOTS_FORWARD_ONLY',
  },
  {
    labelKey: 'interface:properties.highway.connection_options.pivot',
    value: 'MAP_NEIGHBORS_WITH_PIVOTS_FORWARD_ONLY',
  },
];

type Props = {
  selectedHighwayAreas: HighwayArea[];
};

export function HighwayConnectNeighbors({ selectedHighwayAreas }: Props) {
  const optionsOptions = useTranslatedOptions(OPTIONS);
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);

  const value = useMemo(() => {
    const values = selectedHighwayAreas.map((highway) => highway.connectNeighbors);
    return getCommonOption(values);
  }, [selectedHighwayAreas]);

  const onChange = useCallback(
    (newValue: HighwayArea['connectNeighbors']) => {
      const updatedAreas = createEmptyAreas();
      selectedHighwayAreas.forEach((highway) => {
        pushAreaToItsCollection(updatedAreas, { ...highway, connectNeighbors: newValue });
      });
      fddmSvc.setPreference('highwayArea.connectNeighbors', newValue);
      fddmSvc.updateAreas(updatedAreas);
    },
    [selectedHighwayAreas, fddmSvc],
  );

  return <SelectInput options={optionsOptions} onChange={onChange} value={value} showMixed />;
}
